import {
  AssetWithPosition,
  IssuerAggregation,
} from "../data/types/portfolio-statement/portfolio-statement";

/**
 * Groups assets by issuer and calculates total position value per issuer.
 * Returns aggregations sorted by total value descending.
 */
export const aggregateAssetsByIssuer = (
  assets: AssetWithPosition[],
): IssuerAggregation[] => {
  const issuerMap = new Map<string, IssuerAggregation>();

  for (const asset of assets) {
    const existing = issuerMap.get(asset.issuerId);
    if (existing) {
      existing.totalValue += asset.totalPositionValue;
      existing.assets.push(asset);
    } else {
      issuerMap.set(asset.issuerId, {
        issuerId: asset.issuerId,
        issuerName: asset.issuerName,
        totalValue: asset.totalPositionValue,
        assets: [asset],
      });
    }
  }

  const aggregations = Array.from(issuerMap.values());

  // Sort issuers by total value descending
  aggregations.sort((a, b) => b.totalValue - a.totalValue);

  // Sort assets within each issuer by individual position value descending
  for (const agg of aggregations) {
    agg.assets.sort((a, b) => b.totalPositionValue - a.totalPositionValue);
  }

  return aggregations;
};

/**
 * Returns assets ordered by issuer total value (descending),
 * then by individual asset value within each issuer (descending).
 */
export const orderAssetsByIssuerValue = (
  assets: AssetWithPosition[],
): AssetWithPosition[] => {
  const aggregations = aggregateAssetsByIssuer(assets);
  return aggregations.flatMap((agg) => agg.assets);
};

/**
 * Returns the 1-based order number for an asset in the ordered list.
 */
export const getAssetOrderNumber = (
  assetId: string,
  orderedAssets: AssetWithPosition[],
): number => {
  const index = orderedAssets.findIndex((a) => a.id === assetId);
  return index + 1;
};
